import { useEffect } from "react";
import { injectExternalScript } from "@/lib/dom-meta";

type PixelEntry = { provider: string; pixelId: string };

const BASE_URL = (import.meta as any).env?.BASE_URL ?? "/";
const API = (BASE_URL === "/" ? "" : BASE_URL.replace(/\/$/, "")) + "/api";

function initPixels(pixels: PixelEntry[]): void {
  for (const { provider, pixelId } of pixels) {
    const safe = pixelId.replace(/[^a-zA-Z0-9_\-]/g, "");
    try {
      switch (provider) {
        case "ga4":
          if (
            injectExternalScript({
              id: `ga4-loader-${safe}`,
              src: `https://www.googletagmanager.com/gtag/js?id=${pixelId}`,
            })
          ) {
            injectExternalScript({
              id: `ga4-init-${safe}`,
              inline: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${pixelId}');`,
            });
          }
          break;

        case "gtm":
          injectExternalScript({
            id: `gtm-${safe}`,
            inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${pixelId}');`,
          });
          break;

        case "meta":
          injectExternalScript({
            id: `fbq-${safe}`,
            inline: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`,
          });
          break;

        case "tiktok":
          injectExternalScript({
            id: `ttq-${safe}`,
            inline: `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;i++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${pixelId}');ttq.page();}(window,document,'ttq');`,
          });
          break;

        case "snapchat":
          injectExternalScript({
            id: `snaptr-${safe}`,
            inline: `(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script';r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');snaptr('init','${pixelId}');snaptr('track','PAGE_VIEW');`,
          });
          break;

        case "pinterest":
          injectExternalScript({
            id: `pintrk-${safe}`,
            inline: `!function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var n=window.pintrk;n.queue=[],n.version="3.0";var t=document.createElement("script");t.async=!0,t.src=e;var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");pintrk('load','${pixelId}');pintrk('page');`,
          });
          break;

        case "clarity":
          injectExternalScript({
            id: `clarity-${safe}`,
            inline: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${pixelId}");`,
          });
          break;

        case "hotjar":
          injectExternalScript({
            id: `hotjar-${safe}`,
            inline: `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${pixelId},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r)})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
          });
          break;

        default:
          break;
      }
    } catch {}
  }
}

export function PixelLoader(): null {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const w = window as any;

    // If Cloudflare Zaraz is present it handles all pixel injection at the
    // edge — skip client-side injection to avoid double-firing events.
    if (w.zaraz) return;

    // Defer pixel injection well past LCP so it doesn't compete with
    // critical rendering or inflate TBT.  requestIdleCallback fires only
    // when the browser is idle; setTimeout is the fallback for Safari.
    const schedule = (cb: () => void) => {
      if (typeof requestIdleCallback !== "undefined") {
        requestIdleCallback(cb, { timeout: 4000 });
      } else {
        setTimeout(cb, 3000);
      }
    };

    schedule(async () => {
      // Re-check: Zaraz might have initialised asynchronously.
      if ((window as any).zaraz) return;
      try {
        const res = await fetch(`${API}/_meta/pixels`, {
          cache: "default",
          credentials: "omit",
        });
        if (!res.ok) return;
        const data = await res.json();
        const pixels: PixelEntry[] = Array.isArray(data?.pixels) ? data.pixels : [];
        if (pixels.length > 0) initPixels(pixels);
      } catch {}
    });
  }, []);

  return null;
}
